import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { captureException } from '@sentry/nextjs'

import appConfig from '~/config/app'
import useCurrentUser from '~/hooks/useCurrentUser'
import useReloadPage from '~/hooks/useReloadPage'
import useSsrSafeValue from '~/hooks/useSsrSafeValue'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import { useUpdateUserMutation } from '~/redux/apis/user-api'
import { useGetUserSettingsQuery, useSetUserSettingsMutation } from '~/redux/apis/user-settings-api'
import { selectIsProgressMigrationFlowOpen, setStateSwitchModalOpen } from '~/redux/slices/modals'
import ROUTES from '~/shared/constants/routes.const'
import { selectShowHomMethods } from '~/onboarding/redux/onboarding.slice'

import {
  useGetCurrentGradeQueryState,
  useGetCurriculumCountriesQuery,
  useResetGradesMutation,
} from '../apis/curriculum.api'
import {
  findUsCurriculumStateByName,
  findUsCurriculumStateByStateCode,
  getOrderedUsCurriculumStates,
  isDisplayOnlyCurriculumChange,
  US_GROUPED_COUNTRY_CODES,
  US_OTHER_STATES_CODE,
  UsCurriculumState,
} from '../constants/usCurriculumStates.const'
import { setGradeListByCountryActionCreator } from '../slice/curriculum.slice'
import { setCurriculumName } from '../slice/persistCurriculum.slice'
import { setLockRedirectionActionCreator } from '../slice/redirection.slice'
import getLanguageFromCurriculumCode from '../utils/getLanguageFromCurriculumCode'

const US_COUNTRY_CODE = 'US'
// Only over a settled dashboard: never onboarding, the profile picker, or a dashboard still loading.
const DASHBOARD_SETTLE_MS = 1500

interface PromptView {
  availableStates: UsCurriculumState[]
  losesProgress: boolean
  selectedState: UsCurriculumState | undefined
}

interface StateSwitchPrompt {
  availableStates: UsCurriculumState[]
  isSwitching: boolean
  /** Florida leaves the `us` group, so switching resets grades. */
  losesProgress: boolean
  selectedState: UsCurriculumState | undefined
  shouldShow: boolean
  chooseState: (curriculumName: string) => void
  confirm: () => Promise<void>
  dismiss: () => void
}

/**
 * Offers a legacy US account the standard for the state its IP resolves to.
 * Registration always writes a state code, so a null one means the account predates the column.
 */
export function useStateSwitchPrompt(isBlocked = false): StateSwitchPrompt {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const user = useCurrentUser()
  const [updateUser] = useUpdateUserMutation()
  const [setUserSettings] = useSetUserSettingsMutation()
  const [resetGrades] = useResetGradesMutation()
  const reloadPage = useReloadPage()
  const isMigrationFlowOpen = useAppSelector(selectIsProgressMigrationFlowOpen)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [chosenCurriculumName, setChosenCurriculumName] = useState<string>()
  const [frozenView, setFrozenView] = useState<PromptView>()
  const isDashboard = router.pathname === ROUTES.DASHBOARD
  // Read-only: the dashboard owns the grade subscription and its HoM Methods intro.
  const { isSuccess: isGradeLoaded } = useGetCurrentGradeQueryState(undefined)
  const showHomMethods = useAppSelector(selectShowHomMethods)
  const [isSettled, setIsSettled] = useState(false)

  // Modals.tsx never unmounts, so a profile switch must not inherit the previous user's dismissal or pick.
  useEffect(() => {
    setIsDismissed(false)
    setChosenCurriculumName(undefined)
  }, [user?.id])

  const isLegacyUsAccount =
    !appConfig.isSchoolPlatform &&
    US_GROUPED_COUNTRY_CODES.includes(user?.curriculumCountryCode ?? '') &&
    !user?.curriculumStateCode

  // Both lookups stay unsubscribed for everyone else, so the shared caches are untouched.
  const { data: geoLocation } = useGetUserLocationInfoFromIpQuery(undefined, { skip: !isLegacyUsAccount })
  const { data: curriculums } = useGetCurriculumCountriesQuery(undefined, { skip: !isLegacyUsAccount })
  const { data: userSettings } = useGetUserSettingsQuery(undefined, { skip: !isLegacyUsAccount })

  // The catch-all is not a standard to switch to, so the popup never offers it.
  const availableStates = useMemo(
    () =>
      getOrderedUsCurriculumStates(new Set((curriculums ?? []).map(({ countryCode }) => countryCode))).filter(
        ({ stateCode }) => stateCode !== US_OTHER_STATES_CODE,
      ),
    [curriculums],
  )

  const detectedState =
    geoLocation?.countryCode === US_COUNTRY_CODE ? findUsCurriculumStateByStateCode(geoLocation.region) : undefined
  // The catch-all is not a standard we can offer, so an unserved region never opens the prompt.
  const isOfferable =
    Boolean(detectedState) &&
    detectedState?.stateCode !== US_OTHER_STATES_CODE &&
    availableStates.some(({ stateCode }) => stateCode === detectedState?.stateCode)

  const selectedState = findUsCurriculumStateByName(chosenCurriculumName) ?? detectedState
  const losesProgress = Boolean(
    selectedState?.curriculumName &&
      !isDisplayOnlyCurriculumChange(selectedState.curriculumName, user?.curriculumCountryCode),
  )

  // Settings must have loaded, or a user who already said "Not now" sees a flash on every login.
  const isUnseen = Boolean(userSettings) && !userSettings?.stateSwitchModalShown
  const isReadyToShow = Boolean(
    isLegacyUsAccount &&
      isOfferable &&
      isUnseen &&
      isDashboard &&
      isGradeLoaded &&
      !showHomMethods &&
      !isBlocked &&
      !isDismissed &&
      !isMigrationFlowOpen,
  )

  // Give the dashboard a beat to paint; any condition flipping back cancels the timer.
  useEffect(() => {
    if (!isReadyToShow) {
      setIsSettled(false)
      return undefined
    }
    const timeoutId = window.setTimeout(() => setIsSettled(true), DASHBOARD_SETTLE_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isReadyToShow])

  // Geo and the account are unknown server-side. Held open while switching: the save ends the legacy check first.
  const shouldShow = useSsrSafeValue(false, (isReadyToShow && isSettled) || isSwitching)

  // DefaultLayout owns the body scroll lock; writing it here would join the competing writers.
  useEffect(() => {
    dispatch(setStateSwitchModalOpen(shouldShow))

    return () => {
      dispatch(setStateSwitchModalOpen(false))
    }
  }, [dispatch, shouldShow])

  // Closing locally first: the flag is a courtesy across devices, not a gate on the click.
  const dismiss = useCallback((): void => {
    setIsDismissed(true)
    setUserSettings({ stateSwitchModalShown: true })
      .unwrap()
      .catch((error) => captureException(error, { tags: { feature: 'curriculum', action: 'state-switch-dismiss' } }))
  }, [setUserSettings])

  const chooseState = useCallback((curriculumName: string): void => setChosenCurriculumName(curriculumName), [])

  const confirm = useCallback(async (): Promise<void> => {
    // Read before any await: the saved state code skips the lookups, which drops their data mid-switch.
    const curriculumName = selectedState?.curriculumName
    const curriculumCountryCode = selectedState?.curriculumCountryCode
    const curriculumStateCode = selectedState?.stateCode
    if (!curriculumName || !curriculumCountryCode || isSwitching) return

    setFrozenView({ availableStates, losesProgress, selectedState })
    setIsSwitching(true)
    try {
      if (isDisplayOnlyCurriculumChange(curriculumName, user?.curriculumCountryCode)) {
        await updateUser({ data: { curriculumStateCode } }).unwrap()
        dispatch(setCurriculumName(curriculumName))
        setIsDismissed(true)
        // Same content, so a real reload is the only sign of the switch.
        await reloadPage()
        return
      }

      // Saved before the reset, so a failure never wipes grades on the old curriculum.
      dispatch(setLockRedirectionActionCreator(true))
      await updateUser({ data: { curriculumCountryCode, curriculumStateCode } }).unwrap()
      dispatch(setCurriculumName(curriculumName))
      dispatch(setGradeListByCountryActionCreator([]))
      setIsDismissed(true)

      const resetResult = await resetGrades()
      if (!('data' in resetResult) || !resetResult.data?.success) {
        captureException(new Error('Grade reset failed after the state switch'), {
          tags: { feature: 'curriculum', action: 'state-switch-prompt' },
        })
      }

      const locale = getLanguageFromCurriculumCode(curriculumCountryCode)
      await router.push(ROUTES.DASHBOARD, ROUTES.DASHBOARD, { locale })
    } catch (error) {
      captureException(error, { tags: { feature: 'curriculum', action: 'state-switch-prompt' } })
    } finally {
      dispatch(setLockRedirectionActionCreator(false))
      setIsSwitching(false)
      setFrozenView(undefined)
    }
  }, [
    availableStates,
    dispatch,
    isSwitching,
    losesProgress,
    reloadPage,
    resetGrades,
    router,
    selectedState,
    updateUser,
    user?.curriculumCountryCode,
  ])

  return {
    ...(frozenView ?? { availableStates, losesProgress, selectedState }),
    chooseState,
    confirm,
    dismiss,
    isSwitching,
    shouldShow,
  }
}
